import { Box, Text, Tooltip } from '@mantine/core'
import { ReactNode, useEffect, useRef, useState } from 'react'

interface InfoTooltipProps {
  title: ReactNode
  content: ReactNode
  tooltip: ReactNode
  titleProps?: React.ComponentProps<typeof Text>
  contentProps?: React.ComponentProps<typeof Text>
}

export default function InfoTooltip({
  title,
  content,
  tooltip,
  titleProps,
  contentProps,
}: InfoTooltipProps) {
  const labelRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [offsetX, setOffsetX] = useState(0)

  useEffect(() => {
    if (contentRef.current && labelRef.current) {
      setOffsetX((contentRef.current.offsetWidth - labelRef.current.offsetWidth) / 2)
    }
  }, [])

  return (
    <Tooltip
      withArrow
      multiline
      w={180}
      bg={'var(--bg-surface-emphasis)'}
      label={
        <Box maw={180} style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
          <Text variant="subtitle" mb={4} c="var(--content-secondary, --text-secondary)">
            {tooltip}
          </Text>
        </Box>
      }
      position="top"
      withinPortal={false}
      offset={{ mainAxis: 8, crossAxis: -offsetX }}
      events={{ hover: true, focus: true, touch: true }}
    >
      <Box
        style={{
          display: 'inline-block',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          outline: 'none',
          userSelect: 'none',
        }}
      >
        <Text
          ref={labelRef}
          variant="body"
          mb={4}
          w="fit-content"
          c="#A8A8A8"
          display="block"
          style={{
            WebkitTapHighlightColor: 'transparent',
            outline: 'none',
            userSelect: 'none',
          }}
          {...titleProps}
        >
          {title}
        </Text>

        {typeof content === 'string' ? (
          <Text
            variant="body1"
            style={{ display: 'inline-block' }}
            ref={contentRef}
            {...contentProps}
          >
            {content}
          </Text>
        ) : (
          <Box ref={contentRef}>{content}</Box>
        )}
      </Box>
    </Tooltip>
  )
}
